import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, CircleNotch, CheckCircle, Clock, Fire, Circle, CalendarDots } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_CONFIG = {
  urgent: { color: "#FF5A00", label: "URGENT", icon: Fire },
  high: { color: "#FFCC00", label: "HIGH", icon: Circle },
  medium: { color: "#002FA7", label: "MED", icon: Circle },
  low: { color: "#404040", label: "LOW", icon: Circle },
};

function TaskItem({ task, onUpdate }) {
  const priority = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
  const PIcon = priority.icon;
  const isDone = task.status === "completed";

  return (
    <div
      data-testid={`task-item-${task.id}`}
      className={`flex items-start gap-3 p-3 border-b border-[#E5E5E5] last:border-0 ${isDone ? "opacity-50" : ""}`}
    >
      <button
        data-testid={`toggle-task-${task.id}`}
        onClick={() => onUpdate(task.id, isDone ? "pending" : "completed")}
        className="touch-target w-5 h-5 mt-0.5 shrink-0"
      >
        {isDone ? (
          <CheckCircle size={20} weight="fill" className="text-[#34C759]" />
        ) : (
          <Circle size={20} style={{ color: priority.color }} />
        )}
      </button>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${isDone ? "line-through text-[#404040]" : "text-[#0A0A0A]"}`}>
          {task.title}
        </p>
        {task.description && (
          <p className="text-xs text-[#404040] mt-0.5 line-clamp-1">{task.description}</p>
        )}
        <div className="flex items-center gap-2 mt-1">
          <span className="font-mono-data text-[10px]" style={{ color: priority.color }}>
            {priority.label}
          </span>
          {task.due_date && (
            <span className="font-mono-data text-[10px] text-[#404040] flex items-center gap-0.5">
              <Clock size={8} /> {task.due_date}
            </span>
          )}
          {task.assigned_worker && (
            <span className="font-mono-data text-[10px] text-[#404040]">{task.assigned_worker}</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [form, setForm] = useState({ title: "", description: "", priority: "medium", due_date: "", assigned_worker: "" });
  const [saving, setSaving] = useState(false);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/tasks`);
      setTasks(res.data);
    } catch {
      toast.error("Failed to load tasks");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const updateTaskStatus = async (id, status) => {
    try {
      await axios.put(`${API}/tasks/${id}`, { status });
      toast.success(status === "completed" ? "Task completed" : "Task reopened");
      fetchTasks();
    } catch {
      toast.error("Failed to update task");
    }
  };

  const createTask = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/tasks`, form);
      toast.success("Task created");
      setCreateOpen(false);
      setForm({ title: "", description: "", priority: "medium", due_date: "", assigned_worker: "" });
      fetchTasks();
    } catch {
      toast.error("Failed to create task");
    }
    setSaving(false);
  };

  const pendingTasks = tasks.filter(t => t.status !== "completed");
  const completedTasks = tasks.filter(t => t.status === "completed");

  // Dates that have tasks
  const taskDates = tasks
    .filter(t => t.due_date)
    .map(t => new Date(t.due_date));

  return (
    <div className="p-4 max-w-lg mx-auto" data-testid="tasks-page">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-black tracking-tight" style={{ fontFamily: "'Chivo', sans-serif" }}>
          Tasks & Reminders
        </h1>
        <button
          data-testid="create-task-btn"
          onClick={() => setCreateOpen(true)}
          className="touch-target w-10 h-10 bg-[#002FA7] text-white rounded-sm flex items-center justify-center hover:bg-[#001D6C] transition-colors"
        >
          <Plus size={20} weight="bold" />
        </button>
      </div>

      <Tabs defaultValue="list" className="mb-4">
        <TabsList className="grid w-full grid-cols-2 h-9">
          <TabsTrigger value="list" className="text-xs" data-testid="tasks-list-tab">Task List</TabsTrigger>
          <TabsTrigger value="calendar" className="text-xs" data-testid="tasks-calendar-tab">Calendar</TabsTrigger>
        </TabsList>

        <TabsContent value="list">
          {loading ? (
            <div className="flex justify-center py-12">
              <CircleNotch size={28} className="animate-spin text-[#002FA7]" />
            </div>
          ) : (
            <div>
              {pendingTasks.length > 0 && (
                <div className="mb-4">
                  <h2 className="font-mono-data text-[10px] text-[#002FA7] mb-2">
                    ACTIVE ({pendingTasks.length})
                  </h2>
                  <div className="wp-card p-0">
                    {pendingTasks.map(t => (
                      <TaskItem key={t.id} task={t} onUpdate={updateTaskStatus} />
                    ))}
                  </div>
                </div>
              )}
              {completedTasks.length > 0 && (
                <div>
                  <h2 className="font-mono-data text-[10px] text-[#404040] mb-2">
                    COMPLETED ({completedTasks.length})
                  </h2>
                  <div className="wp-card p-0">
                    {completedTasks.map(t => (
                      <TaskItem key={t.id} task={t} onUpdate={updateTaskStatus} />
                    ))}
                  </div>
                </div>
              )}
              {tasks.length === 0 && (
                <div className="wp-card text-center py-8 text-sm text-[#404040]">
                  No tasks yet. Create your first task.
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="calendar">
          <div className="wp-card flex justify-center">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(d) => d && setSelectedDate(d)}
              modifiers={{ hasTasks: taskDates }}
              modifiersStyles={{ hasTasks: { fontWeight: "bold", textDecoration: "underline", color: "#002FA7" } }}
            />
          </div>
          {/* Tasks for selected date */}
          <div className="mt-3">
            <h3 className="font-mono-data text-[10px] text-[#404040] mb-2 flex items-center gap-1">
              <CalendarDots size={10} />
              {selectedDate.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
            </h3>
            <div className="wp-card p-0">
              {tasks.filter(t => t.due_date === selectedDate.toISOString().split('T')[0]).length === 0 ? (
                <p className="text-xs text-[#404040] p-3">No tasks for this date</p>
              ) : (
                tasks.filter(t => t.due_date === selectedDate.toISOString().split('T')[0]).map(t => (
                  <TaskItem key={t.id} task={t} onUpdate={updateTaskStatus} />
                ))
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>New Task</DialogTitle>
          </DialogHeader>
          <form onSubmit={createTask} className="space-y-3">
            <input
              data-testid="task-title-input"
              type="text" placeholder="Task title"
              value={form.title} onChange={e => setForm({...form, title: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]"
            />
            <textarea
              data-testid="task-description-input"
              placeholder="Description (optional)"
              value={form.description} onChange={e => setForm({...form, description: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7] resize-none h-16"
            />
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}>
                <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
              <input
                data-testid="task-due-date-input"
                type="date"
                value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})}
                className="px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]"
              />
            </div>
            <input
              data-testid="task-worker-input"
              type="text" placeholder="Assigned worker (optional)"
              value={form.assigned_worker} onChange={e => setForm({...form, assigned_worker: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]"
            />
            <button
              data-testid="create-task-submit"
              type="submit" disabled={saving}
              className="w-full py-2.5 bg-[#002FA7] text-white text-sm font-bold rounded-sm hover:bg-[#001D6C] transition-colors disabled:opacity-50"
            >
              {saving ? "Creating..." : "Create Task"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
